import { cn } from "../ui/cn";
import { MultiSelect } from "../ui/MultiSelect";
import type { EditorField } from "./rowEditor";

const CONTROL =
  "h-7 w-full rounded border border-line bg-surface px-1.5 text-[13px] text-ink focus-visible:outline-1 focus-visible:outline-accent";

/**
 * One field of the row being edited.
 *
 * Enter commits and Escape cancels, which is what a keyboard-driven data-entry
 * user reaches for first. Tab moves through the row's fields normally; the
 * table handles what happens when Tab leaves the last one.
 */
export function EditorCell<T>({
  field,
  draft,
  error,
  onChange,
  onCommit,
  onCancel,
  autoFocus,
  isLast,
  onLeaveLast,
}: {
  field: EditorField<T>;
  draft: T;
  error?: string | undefined;
  onChange: (next: T) => void;
  onCommit: () => void;
  onCancel: () => void;
  autoFocus?: boolean;
  isLast?: boolean;
  onLeaveLast?: () => void;
}) {
  const keys = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onCommit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    } else if (event.key === "Tab" && !event.shiftKey && isLast && onLeaveLast) {
      // Tabbing off the last field saves and opens the next row — the
      // continuous data-entry flow the Vaadin editor grids had.
      event.preventDefault();
      onLeaveLast();
    }
  };

  const invalid = Boolean(error);
  const className = cn(CONTROL, invalid && "border-risk focus-visible:outline-risk");

  if (field.kind === "checkbox") {
    return (
      <input
        type="checkbox"
        checked={field.get(draft)}
        autoFocus={autoFocus}
        onChange={(event) => onChange(field.set(draft, event.target.checked))}
        onKeyDown={keys}
        className="size-3.5 accent-[var(--accent)]"
      />
    );
  }

  if (field.kind === "multiselect") {
    return (
      <div className="min-w-40" onKeyDown={keys}>
        <MultiSelect
          options={field.options}
          value={field.get(draft)}
          onChange={(next) => onChange(field.set(draft, next))}
        />
      </div>
    );
  }

  if (field.kind === "select") {
    return (
      <select
        value={field.get(draft) ?? ""}
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        title={error}
        onChange={(event) => onChange(field.set(draft, event.target.value))}
        onKeyDown={keys}
        className={className}
      >
        <option value="" disabled={field.required}>
          
        </option>
        {(typeof field.options === "function" ? field.options(draft) : field.options).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type={field.kind === "number" ? "number" : field.kind === "email" ? "email" : "text"}
      value={String(field.get(draft) ?? "")}
      autoFocus={autoFocus}
      aria-invalid={invalid || undefined}
      title={error}
      placeholder={field.placeholder ?? ""}
      onChange={(event) => onChange(field.set(draft, event.target.value))}
      onKeyDown={keys}
      className={className}
    />
  );
}
